import { useEffect, useMemo, useState } from 'react'
import { organizationAPI, type OrganizationExpanded } from '#/api/organization'

import { useQuery } from '@tanstack/react-query'

import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  CommandLoading
} from '#/components/ui/command'
import { useNavigate } from '#/hooks/use-navigate'
import { useTranslation } from 'react-i18next'
import { chargerAPI } from '#/api/charger'
import { namespaceAPI } from '#/api/namespace'


enum CommandPaletteLists {
  Partners = 'PARTNERS',
  Customers = 'CUSTOMERS',
  Chargers = 'CHARGERS',
  Accounts = 'ACCOUNTS'
}

interface CmdItem {
  id: string
  title: string
  href: string
  keywords: string[]
}

interface CmdList {
  heading: string
  id: CommandPaletteLists
  items: CmdItem[]
}

interface CustomCommandPaletteProps {
  organization: OrganizationExpanded
  setIsCmdkOpen: (open: boolean | ((current: boolean) => boolean)) => void
  isCmdkOpen: boolean
}

export default function CustomCommandPalette({ organization, setIsCmdkOpen, isCmdkOpen }: CustomCommandPaletteProps) {
  const { t } = useTranslation([ 'general' ])
  const navigate = useNavigate()

  // open cmdk on cmd+k


  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault()
        setIsCmdkOpen(open => !open)
      }
    }

    document.addEventListener('keydown', down)

    return () => document.removeEventListener('keydown', down)
  }, [ setIsCmdkOpen ])


  // fetch organizations on search value
  const [ searchString, setSearchString ] = useState('')

  const orgsQuery = useQuery({
    queryKey: [ 'organizations', organization.id, 'partners', 'search', searchString ],
    queryFn: () => organizationAPI.listOrganizations(organization.id, { search: { in: 'organization', value: searchString } }),
    enabled: searchString.length >= 4
  })

  const searchablePartners = useMemo(() => {
    if (orgsQuery.data === undefined) return []

    const partners = orgsQuery.data.organizations.map(partner => ({
      id: partner.id,
      title: partner.name,
      keywords: [ partner.name, partner.id, partner.shortId ],
      href: `/${organization.shortId}/partners/${partner.shortId}`
    }))

    return partners
  }, [ orgsQuery.data, organization.shortId ])

  // fetch customers on search value

  const namespacesQuery = useQuery({
    queryKey: [ 'organizations', organization.id, 'customers', 'search', searchString ],
    queryFn: () => organizationAPI.listNamespaces(organization.id, { search: { in: 'namespace', value: searchString } }),
    enabled: searchString.length >= 4
  })

  const searchableCustomers = useMemo(() => {
    if (namespacesQuery.data === undefined) return []

    const customers = namespacesQuery.data.namespaces.map(customer => ({
      id: customer.id,
      title: customer.name,
      keywords: [ customer.name, customer.id, customer.shortId ],
      href: `/${organization.shortId}/customers/${customer.shortId}`
    }))

    return customers
  }, [ namespacesQuery.data, organization.shortId ])

  // fetch chargers on search value

  const chargersQuery = useQuery({
    queryKey: [ 'organizations', organization.id, 'chargers', 'search', searchString ],
    queryFn: () => chargerAPI.listChargers(organization.id, {
      chargerListSelector: {
        id: { search: searchString },
        name: { search: searchString },
        chargePointSerialNumber: { search: searchString }
      }
    }),
    enabled: searchString.length >= 4
  })

  const searchableChargers = useMemo(() => {
    if (chargersQuery.data === undefined) return []

    const chargersData = chargersQuery.data.chargers

    const namespaces = chargersData.map(charger => charger.namespaceId)
    const uniqueNamespaces = [ ...(new Set(namespaces)) ]
    const base = globalThis.location.origin
    const namespacesWithUrls = uniqueNamespaces
      .map(namespaceId => ({ nsId: namespaceId, url: base + namespaceAPI.getImpersonationURL(namespaceId) }))

    const chargers = chargersQuery.data.chargers.map(charger => {
      return charger.initialized ? ({
        id: charger.id,
        title: charger.name,
        keywords: [ charger.name, charger.id, charger.metadata.chargePointSerialNumber ],
        href: namespacesWithUrls.filter(ns => ns.nsId === charger.namespaceId).map(ns => ns.url)[0]
      })
        : ({
          id: charger.id,
          title: charger.name,
          keywords: [ charger.name, charger.id ],
          href: namespacesWithUrls.filter(ns => ns.nsId === charger.namespaceId).map(ns => ns.url)[0]
        })
    })

    return chargers
  }, [ chargersQuery.data ])

  // set variables affected by queries
  const isLoading = useMemo(() => {
    return namespacesQuery.isFetching || orgsQuery.isFetching || chargersQuery.isFetching
  }, [ namespacesQuery.isFetching, orgsQuery.isFetching, chargersQuery.isFetching ])

  const itemLists = useMemo(() => {
    const lists: CmdList[] = [
      {
        heading: 'Partners',
        id: CommandPaletteLists.Partners,
        items: searchablePartners as CmdItem[]
      },
      {
        heading: 'Customers',
        id: CommandPaletteLists.Customers,
        items: searchableCustomers as CmdItem[]
      },
      {
        heading: 'Chargers',
        id: CommandPaletteLists.Chargers,
        items: searchableChargers as CmdItem[]
      }/* ,
      {
        heading: 'Accounts',
        id: CommandPaletteLists.Accounts,
        items: []
      } */
    ]

    return lists
  }, [ searchablePartners, searchableCustomers, searchableChargers ])


  // Navigate to organization and close dialog
  function select(value: string) {
    setIsCmdkOpen(false)
    if (value.includes('http')) {
      window.open(value, '_blank')
    } else {
      navigate(value)
    }
  }

  return (
    <CommandDialog
      open={isCmdkOpen}
      onOpenChange={setIsCmdkOpen}
      title={t('custom_cmdk_palette.title')}
      description={t('custom_cmdk_palette.description')}
    >
      <CommandInput placeholder={t('custom_cmdk_palette.placeholder')} onValueChange={setSearchString} />
      <CommandList>
        {isLoading && <CommandLoading>{t('custom_cmdk_palette.loading')}</CommandLoading>}
        {!isLoading && <CommandEmpty>{t('custom_cmdk_palette.empty')}</CommandEmpty>}
        {itemLists.map(list => (
          <>
            {list.items.length > 0 && <CommandSeparator />}
            {list.items.length > 0 && <CommandGroup key={list.id} heading={list.heading}>
              {list.items.map(({ id, title, href, keywords }) => (
                <CommandItem
                  key={id}
                  value={`${id}${href}`}
                  onSelect={value => select(value.replace(id, ''))}
                  keywords={keywords}
                >
                  {title}
                </CommandItem>
              ))}
            </CommandGroup>}
          </>
        ))}
      </CommandList>
    </CommandDialog >
  )

}
